import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Lottie from "react-lottie-player";
import styled, { css } from "styled-components";

const DASHBOARD_THEME_STORAGE_KEY = "dailyfixme.calendar.theme";

export const themeTokens = {
    light: {
        background: "#f5f7fb",
        shell: "#ffffff",
        surface: "#ffffff",
        surfaceSecondary: "#f3f5fb",
        surfaceTertiary: "#eef2f9",
        border: "#d8deec",
        borderStrong: "#c9d2e7",
        inputBorder: "#95a6c8",
        inputShadow: "0px 3px 6px rgba(0, 0, 0, 0.12)",
        text: "#171a22",
        textMuted: "#6a7387",
        textSoft: "#8a95ab",
        sectionLabel: "#8a95ab",
        accent: "#171a22",
        accentSoft: "rgba(91,110,245,0.12)",
        selectedCellBackground: "rgba(91,110,245,0.06)",
        selectedCellBorder: "#5b6ef5",
        toggleActiveBackground: "#5b6ef5",
        toggleActiveThumb: "#ffffff",
        actionPrimaryBackground: "#5b6ef5",
        actionPrimaryText: "#ffffff",
        darkControlText: "#ffffff",
        darkInk: "#171A22",
        previewArrowBackground: "transparent",
        previewArrowFilter: "none",
        todayMarkerBackground: "#2563eb",
        todayMarkerText: "#ffffff",
        shadow: "0 18px 36px rgba(29, 42, 89, 0.08)",
        calendarCell: "#ffffff",
        calendarCellAlt: "#f8faff",
    },
    dark: {
        background: "#0b0d15",
        shell: "#10131d",
        surface: "#161a24",
        surfaceSecondary: "#1b2030",
        surfaceTertiary: "#202638",
        border: "#252b3e",
        borderStrong: "#303850",
        inputBorder: "#46516b",
        inputShadow: "0px 3px 6px rgba(0, 0, 0, 0.12)",
        text: "#f3f6fb",
        textMuted: "#98a2b9",
        textSoft: "#768098",
        sectionLabel: "#c6cede",
        accent: "#f3f6fb",
        accentSoft: "rgba(109,116,255,0.18)",
        selectedCellBackground: "rgba(109,116,255,0.09)",
        selectedCellBorder: "#6d74ff",
        toggleActiveBackground: "#6d74ff",
        toggleActiveThumb: "#000000",
        actionPrimaryBackground: "#5b6ef5",
        actionPrimaryText: "#ffffff",
        darkControlText: "#F3F6FB",
        darkInk: "#171A22",
        previewArrowBackground: "#FFFFFF",
        previewArrowFilter: "invert(1)",
        todayMarkerBackground: "#60a5fa",
        todayMarkerText: "#07111f",
        shadow: "0 24px 44px rgba(0, 0, 0, 0.35)",
        calendarCell: "#171b25",
        calendarCellAlt: "#141822",
    },
};

const CALENDAR_RADIUS = {
    panel: "16px",
    cell: "16px",
    default: "8px",
    toggle: "999px",
};

const THEME_TOGGLE_LIGHT_FRAME = 70;
const THEME_TOGGLE_DARK_FRAME = 0;
const THEME_TOGGLE_DARK_TO_LIGHT_SEGMENT = [THEME_TOGGLE_DARK_FRAME, THEME_TOGGLE_LIGHT_FRAME];
const THEME_TOGGLE_LIGHT_TO_DARK_SEGMENT = [THEME_TOGGLE_LIGHT_FRAME, 140];

const getStorage = () => {
    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
};

export const getDashboardThemeMode = () => {
    const storedTheme = getStorage()?.getItem(DASHBOARD_THEME_STORAGE_KEY);
    return storedTheme === "dark" || storedTheme === "light" ? storedTheme : "light";
};

export const saveDashboardThemeMode = (themeMode) => {
    getStorage()?.setItem(DASHBOARD_THEME_STORAGE_KEY, themeMode);
};

const getThemeVariables = (themeMode) => {
    const t = themeTokens[themeMode === "dark" ? "dark" : "light"];
    return {
        "--calendar-bg": t.background,
        "--calendar-shell": t.shell,
        "--calendar-surface": t.surface,
        "--calendar-surface-secondary": t.surfaceSecondary,
        "--calendar-surface-tertiary": t.surfaceTertiary,
        "--calendar-border": t.border,
        "--calendar-border-strong": t.borderStrong,
        "--calendar-input-border": t.inputBorder,
        "--calendar-input-shadow": t.inputShadow,
        "--calendar-text": t.text,
        "--calendar-text-muted": t.textMuted,
        "--calendar-text-soft": t.textSoft,
        "--calendar-section-label": t.sectionLabel,
        "--calendar-accent": t.accent,
        "--calendar-accent-soft": t.accentSoft,
        "--calendar-selected-cell-bg": t.selectedCellBackground,
        "--calendar-selected-cell-border": t.selectedCellBorder,
        "--calendar-toggle-active-bg": t.toggleActiveBackground,
        "--calendar-toggle-active-thumb": t.toggleActiveThumb,
        "--calendar-action-primary-bg": t.actionPrimaryBackground,
        "--calendar-action-primary-text": t.actionPrimaryText,
        "--calendar-dark-control-text": t.darkControlText,
        "--calendar-dark-ink": t.darkInk,
        "--calendar-preview-arrow-bg": t.previewArrowBackground,
        "--calendar-preview-arrow-filter": t.previewArrowFilter,
        "--calendar-today-marker-bg": t.todayMarkerBackground,
        "--calendar-today-marker-text": t.todayMarkerText,
        "--calendar-shadow": t.shadow,
        "--calendar-cell": t.calendarCell,
        "--calendar-cell-alt": t.calendarCellAlt,
        "--calendar-radius-shell": CALENDAR_RADIUS.panel,
        "--calendar-radius-card": CALENDAR_RADIUS.default,
        "--calendar-radius-control": CALENDAR_RADIUS.default,
        "--calendar-radius-input": CALENDAR_RADIUS.default,
        "--calendar-radius-badge": CALENDAR_RADIUS.default,
        "--calendar-radius-task": CALENDAR_RADIUS.default,
        "--calendar-radius-cell": CALENDAR_RADIUS.cell,
        "--calendar-radius-toggle": CALENDAR_RADIUS.toggle,
        "--calendar-radius-round": CALENDAR_RADIUS.toggle,
    };
};

export const dashboardTheme = ({ themeMode }) => {
    const variables = getThemeVariables(themeMode);
    return css`
        ${Object.entries(variables).map(([name, value]) => `${name}: ${value};`).join("\n")}
    `;
};

const DashboardThemeContext = createContext({
    themeMode: "light",
    isDarkMode: false,
    setThemeMode: () => {},
    toggleThemeMode: () => {},
});

const ThemeScope = styled.div`
    ${dashboardTheme};
    min-height: 100%;
    color: var(--calendar-text);
`;

export function DashboardThemeProvider({ children }) {
    const [themeMode, setThemeMode] = useState(getDashboardThemeMode);

    useEffect(() => {
        saveDashboardThemeMode(themeMode);
        const root = document.documentElement;
        const variables = getThemeVariables(themeMode);
        Object.entries(variables).forEach(([name, value]) => root.style.setProperty(name, value));
        root.style.setProperty("color-scheme", themeMode === "dark" ? "dark" : "light");
    }, [themeMode]);

    const value = useMemo(() => ({
        themeMode,
        isDarkMode: themeMode === "dark",
        setThemeMode,
        toggleThemeMode: () => setThemeMode(prev => prev === "light" ? "dark" : "light"),
    }), [themeMode]);

    return (
        <DashboardThemeContext.Provider value={value}>
            <ThemeScope themeMode={themeMode}>
                {children}
            </ThemeScope>
        </DashboardThemeContext.Provider>
    );
}

export const useDashboardTheme = () => useContext(DashboardThemeContext);

const ThemeToggleButton = styled.button`
    height: ${p => p.$compact ? "32px" : "42px"};
    width: ${p => p.$compact ? "58px" : "78px"};
    border-radius: var(--calendar-radius-control);
    border: 1px solid ${p => p.$nav ? "rgba(255, 255, 255, 0.16)" : "var(--calendar-border)"};
    background: ${p => p.$nav ? "rgba(255, 255, 255, 0.08)" : "var(--calendar-surface-secondary)"};
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: visible;

    &:hover {
        border-color: ${p => p.$nav ? "rgba(255, 255, 255, 0.26)" : "var(--calendar-border-strong)"};
        background: ${p => p.$nav ? "rgba(255, 255, 255, 0.13)" : "var(--calendar-surface-tertiary)"};
    }
`;

export function DashboardThemeToggle({ compact = false, nav = false, className }) {
    const { themeMode, setThemeMode } = useDashboardTheme();
    const [animationData, setAnimationData] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [frame, setFrame] = useState(themeMode === "dark" ? THEME_TOGGLE_DARK_FRAME : THEME_TOGGLE_LIGHT_FRAME);
    const [segment, setSegment] = useState(null);
    const [holdFinalFrame, setHoldFinalFrame] = useState(false);
    const [runId, setRunId] = useState(0);

    useEffect(() => {
        let cancelled = false;

        fetch("/daynight-switch.json")
            .then(response => response.ok ? response.json() : Promise.reject(new Error("Failed to load theme toggle animation.")))
            .then(data => {
                if (!cancelled) setAnimationData(data);
            })
            .catch(error => console.error(error));

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!playing && !holdFinalFrame) {
            setFrame(themeMode === "dark" ? THEME_TOGGLE_DARK_FRAME : THEME_TOGGLE_LIGHT_FRAME);
        }
    }, [holdFinalFrame, playing, themeMode]);

    const handleToggle = () => {
        const nextMode = themeMode === "light" ? "dark" : "light";
        setHoldFinalFrame(false);
        setSegment(nextMode === "dark" ? THEME_TOGGLE_LIGHT_TO_DARK_SEGMENT : THEME_TOGGLE_DARK_TO_LIGHT_SEGMENT);
        setPlaying(true);
        setRunId(prev => prev + 1);
        setThemeMode(nextMode);
    };

    return (
        <ThemeToggleButton
            type="button"
            className={className}
            $compact={compact}
            $nav={nav}
            onClick={handleToggle}
            aria-label={`Switch to ${themeMode === "light" ? "dark" : "light"} mode`}
            aria-pressed={themeMode === "dark"}
            title={`${themeMode === "light" ? "Dark" : "Light"} mode`}
        >
            {animationData && (
                <Lottie
                    key={runId}
                    animationData={animationData}
                    play={playing}
                    loop={false}
                    segments={segment || [frame, frame]}
                    goTo={playing || holdFinalFrame ? undefined : frame}
                    onComplete={() => {
                        if (themeMode === "dark") {
                            setPlaying(false);
                            setHoldFinalFrame(true);
                            return;
                        }
                        setPlaying(false);
                        setHoldFinalFrame(false);
                        setFrame(THEME_TOGGLE_LIGHT_FRAME);
                    }}
                    style={{
                        width: compact ? 108 : 148,
                        height: compact ? 58 : 80,
                        pointerEvents: "none",
                    }}
                />
            )}
        </ThemeToggleButton>
    );
}
